import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useFlag } from '@unleash/proxy-client-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import dayjs from 'dayjs';
import {
  Users, Activity, Flame, Ban, Sparkles, Upload, Play, Lock, X,
  ShieldCheck, ShieldAlert, ShieldX, MoreVertical, Wifi, Server, Smartphone,
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';

export interface FarmAccount {
  id: string;
  username: string;
  status: 'active' | 'warming' | 'banned' | 'recovered' | 'idle';
  proxy: { type: 'residential' | 'datacenter' | 'mobile'; country?: string; provider?: string };
  warming_day: number; // 0..30
  last_action_at: string;
  farm_id?: string;
}

export interface FarmStats {
  total: number;
  active: number;
  warming: number;
  banned: number;
  recovered_today: number;
  deltas: { total: number; active: number; warming: number; banned: number; recovered_today: number };
}

export interface BanRateInfo {
  rate: number; // 0..1
  paused: boolean;
}

export interface ProxyPoolStats {
  providers: Array<{
    name: string;
    kind: 'residential' | 'datacenter' | 'mobile';
    success_rate: number;
    cost_today: number;
  }>;
}

interface FarmOption {
  id: string;
  name: string;
  health: 'green' | 'yellow' | 'red';
}

async function fetchAccounts(farmId?: string): Promise<FarmAccount[]> {
  const { data } = await api.get<FarmAccount[]>('/farm/accounts', { params: { farm_id: farmId } });
  return data;
}

async function fetchStats(farmId?: string): Promise<FarmStats> {
  const { data } = await api.get<FarmStats>('/farm/accounts/stats', { params: { farm_id: farmId } });
  return data;
}

async function fetchBanRate(farmId?: string): Promise<BanRateInfo> {
  const { data } = await api.get<BanRateInfo>('/farm/health/ban-rate', { params: { farm_id: farmId } });
  return data;
}

async function fetchProxyPool(): Promise<ProxyPoolStats> {
  const { data } = await api.get<ProxyPoolStats>('/farm/proxy-pool');
  return data;
}

async function fetchFarms(): Promise<FarmOption[]> {
  const { data } = await api.get<FarmOption[]>('/farm/list');
  return data;
}

function StatusBadge({ status }: { status: FarmAccount['status'] }): JSX.Element {
  const { t } = useTranslation();
  const map: Record<FarmAccount['status'], string> = {
    active: 'bg-emerald-100 text-emerald-700',
    warming: 'bg-amber-100 text-amber-700',
    banned: 'bg-red-100 text-red-700',
    recovered: 'bg-blue-100 text-blue-700',
    idle: 'bg-slate-100 text-slate-600',
  };
  return (
    <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', map[status])}>
      {t(`farm.status.${status}`)}
    </span>
  );
}

function HealthDot({ health }: { health: 'green' | 'yellow' | 'red' }): JSX.Element {
  const Icon = health === 'green' ? ShieldCheck : health === 'yellow' ? ShieldAlert : ShieldX;
  const color = health === 'green' ? 'text-emerald-600' : health === 'yellow' ? 'text-amber-600' : 'text-red-600';
  return <Icon className={color} size={20} aria-label={`health-${health}`} />;
}

interface StatCardProps {
  icon: typeof Users;
  label: string;
  value: number;
  delta: number;
  tone?: 'default' | 'danger' | 'warning' | 'success';
}

function StatCard({ icon: Icon, label, value, delta, tone = 'default' }: StatCardProps): JSX.Element {
  const toneClass = {
    default: 'bg-brand-primary/10 text-brand-primary',
    danger: 'bg-red-100 text-red-600',
    warning: 'bg-amber-100 text-amber-600',
    success: 'bg-emerald-100 text-emerald-600',
  }[tone];
  return (
    <div className="card">
      <div className="flex items-center justify-between mb-2">
        <div className={clsx('p-2 rounded-lg', toneClass)}>
          <Icon size={18} />
        </div>
        <div className={clsx('text-xs font-medium', delta >= 0 ? 'text-emerald-600' : 'text-brand-danger')}>
          {delta >= 0 ? '+' : ''}{delta.toFixed(1)}%
        </div>
      </div>
      <div className="text-2xl font-bold text-brand-ink">{value.toLocaleString()}</div>
      <div className="text-xs text-brand-muted mt-1">{label}</div>
    </div>
  );
}

function ExplainerCard(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="p-6" data-testid="farm-locked">
      <div className="card max-w-2xl mx-auto text-center">
        <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-4">
          <Lock className="text-amber-600" size={32} />
        </div>
        <h2 className="text-2xl font-bold text-brand-ink mb-2">{t('farm.locked.title')}</h2>
        <p className="text-brand-muted mb-6">{t('farm.locked.description')}</p>
        <a href="/billing" className="btn-primary inline-block">{t('farm.locked.upgrade')}</a>
      </div>
    </div>
  );
}

const SCRIPT_LIBRARY = [
  { id: 'warm', endpoint: '/farm/warming/start' },
  { id: 'invite_conservative', endpoint: '/farm/invite', payload: { mode: 'conservative' } },
  { id: 'invite_aggressive', endpoint: '/farm/invite', payload: { mode: 'aggressive' } },
  { id: 'comment', endpoint: '/farm/comment' },
  { id: 'react', endpoint: '/farm/react' },
  { id: 'stories_mention', endpoint: '/farm/stories-mention' },
] as const;

interface AccountDetailsPanelProps {
  account: FarmAccount;
  onClose: () => void;
}

function AccountDetailsPanel({ account, onClose }: AccountDetailsPanelProps): JSX.Element {
  const { t } = useTranslation();
  const recoverMut = useMutation({
    mutationFn: () => api.post(`/farm/accounts/${account.id}/recover`),
    onSuccess: () => toast.success(t('farm.toast.recoverQueued')),
    onError: () => toast.error(t('farm.toast.recoverFailed')),
  });

  return (
    <aside
      className="fixed top-0 right-0 h-full w-full max-w-md bg-white border-l border-slate-200 shadow-xl p-6 overflow-y-auto z-40"
      data-testid="account-details-panel"
      role="dialog"
      aria-label={t('farm.details.title')}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-brand-ink">{account.username}</h3>
        <button aria-label={t('common.cancel')} onClick={onClose} className="p-1.5 rounded hover:bg-slate-100">
          <X size={16} />
        </button>
      </div>
      <div className="space-y-4 text-sm">
        <div>
          <div className="text-xs text-brand-muted mb-1">{t('farm.details.status')}</div>
          <StatusBadge status={account.status} />
        </div>
        <div>
          <div className="text-xs text-brand-muted mb-1">{t('farm.details.proxy')}</div>
          <div className="text-brand-ink">
            {account.proxy.type} · {account.proxy.provider ?? '—'} · {account.proxy.country ?? '—'}
          </div>
        </div>
        <div>
          <div className="text-xs text-brand-muted mb-1">{t('farm.details.warming')}</div>
          <div className="text-brand-ink">{account.warming_day} / 30</div>
        </div>
        <div>
          <div className="text-xs text-brand-muted mb-1">{t('farm.details.lastAction')}</div>
          <div className="text-brand-ink">{dayjs(account.last_action_at).format('DD MMM HH:mm')}</div>
        </div>
        <div className="card bg-slate-50">
          <div className="text-xs text-brand-muted mb-2">{t('farm.details.activity')}</div>
          <div className="h-24 flex items-end gap-1">
            {Array.from({ length: 14 }).map((_, i) => (
              <div
                key={i}
                className="flex-1 bg-brand-primary/70 rounded-t"
                style={{ height: `${20 + ((i * 7) % 60)}%` }}
              />
            ))}
          </div>
        </div>
        <button
          onClick={() => recoverMut.mutate()}
          disabled={recoverMut.isPending || account.status !== 'banned'}
          className="btn-primary w-full disabled:opacity-50"
        >
          {t('farm.details.recover')}
        </button>
      </div>
    </aside>
  );
}

export function Farm(): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const flagEnabled = useFlag('teleclaw_farm_enabled');
  const qc = useQueryClient();

  const visible = Boolean(user?.farmEnabled) && flagEnabled;
  const [selectedFarm, setSelectedFarm] = useState<string | undefined>(undefined);
  const [activeAccount, setActiveAccount] = useState<FarmAccount | null>(null);
  const [banAlertShown, setBanAlertShown] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const farms = useQuery({
    queryKey: ['farm', 'list'],
    queryFn: fetchFarms,
    enabled: visible,
    retry: false,
  });

  const accounts = useQuery({
    queryKey: ['farm', 'accounts', selectedFarm],
    queryFn: () => fetchAccounts(selectedFarm),
    enabled: visible,
    refetchInterval: 30_000,
    retry: false,
  });

  const stats = useQuery({
    queryKey: ['farm', 'stats', selectedFarm],
    queryFn: () => fetchStats(selectedFarm),
    enabled: visible,
    refetchInterval: 30_000,
    retry: false,
  });

  const banRate = useQuery({
    queryKey: ['farm', 'ban-rate', selectedFarm],
    queryFn: () => fetchBanRate(selectedFarm),
    enabled: visible,
    refetchInterval: 30_000,
    retry: false,
  });

  const proxyPool = useQuery({
    queryKey: ['farm', 'proxy-pool'],
    queryFn: fetchProxyPool,
    enabled: visible,
    refetchInterval: 60_000,
    retry: false,
  });

  const importMut = useMutation({
    mutationFn: async (files: FileList) => {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append('files', f));
      if (selectedFarm) fd.append('farm_id', selectedFarm);
      await api.post('/farm/accounts/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    onSuccess: () => {
      toast.success(t('farm.toast.imported'));
      qc.invalidateQueries({ queryKey: ['farm', 'accounts'] });
    },
    onError: () => toast.error(t('farm.toast.importFailed')),
  });

  const runScriptMut = useMutation({
    mutationFn: async (s: typeof SCRIPT_LIBRARY[number]) => {
      const payload = { farm_id: selectedFarm, ...(s as { payload?: Record<string, unknown> }).payload };
      await api.post(s.endpoint, payload);
    },
    onSuccess: () => toast.success(t('farm.toast.scriptStarted')),
    onError: () => toast.error(t('farm.toast.scriptFailed')),
  });

  // Ban alert effect
  useEffect(() => {
    if (banRate.data && banRate.data.rate > 0.10 && !banAlertShown) {
      toast.error(t('farm.alert.banSpike'), { duration: 8000, id: 'farm-ban-alert' });
      setBanAlertShown(true);
    } else if (banRate.data && banRate.data.rate <= 0.10 && banAlertShown) {
      setBanAlertShown(false);
    }
  }, [banRate.data, banAlertShown, t]);

  const farmHealth: 'green' | 'yellow' | 'red' = useMemo(() => {
    if (!banRate.data) return 'green';
    if (banRate.data.rate > 0.10) return 'red';
    if (banRate.data.rate > 0.05) return 'yellow';
    return 'green';
  }, [banRate.data]);

  if (!visible) {
    return <ExplainerCard />;
  }

  const onFilePick = (e: ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files && e.target.files.length > 0) {
      importMut.mutate(e.target.files);
    }
  };

  return (
    <div className="p-6" data-testid="farm-screen">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-brand-dark">{t('farm.title')}</h1>
          {farms.data && farms.data.length > 1 && (
            <select
              data-testid="farm-selector"
              value={selectedFarm ?? ''}
              onChange={(e) => setSelectedFarm(e.target.value || undefined)}
              className="border border-slate-200 rounded-md px-2 py-1 text-sm"
              aria-label={t('farm.selector')}
            >
              <option value="">{t('farm.all')}</option>
              {farms.data.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          )}
          <HealthDot health={farmHealth} />
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            data-testid="import-input"
            onChange={onFilePick}
            accept=".session,.tdata,.zip"
          />
          <button
            className="btn-secondary inline-flex items-center gap-2"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload size={16} />
            {t('farm.importSessions')}
          </button>
          <button
            className="btn-primary inline-flex items-center gap-2"
            onClick={() => runScriptMut.mutate(SCRIPT_LIBRARY[0])}
            disabled={runScriptMut.isPending}
          >
            <Play size={16} />
            {t('farm.runScript')}
          </button>
        </div>
      </div>

      {/* Hero stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <StatCard icon={Users} label={t('farm.cards.total')} value={stats.data?.total ?? 0} delta={stats.data?.deltas.total ?? 0} />
        <StatCard icon={Activity} label={t('farm.cards.active')} value={stats.data?.active ?? 0} delta={stats.data?.deltas.active ?? 0} tone="success" />
        <StatCard icon={Flame} label={t('farm.cards.warming')} value={stats.data?.warming ?? 0} delta={stats.data?.deltas.warming ?? 0} tone="warning" />
        <StatCard icon={Ban} label={t('farm.cards.banned')} value={stats.data?.banned ?? 0} delta={stats.data?.deltas.banned ?? 0} tone="danger" />
        <StatCard icon={Sparkles} label={t('farm.cards.recovered')} value={stats.data?.recovered_today ?? 0} delta={stats.data?.deltas.recovered_today ?? 0} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left: Script Library */}
        <aside className="card lg:col-span-1">
          <h3 className="text-lg font-semibold text-brand-ink mb-3">{t('farm.scripts.title')}</h3>
          <ul className="space-y-2" data-testid="script-library">
            {SCRIPT_LIBRARY.map((s) => (
              <li key={s.id}>
                <button
                  onClick={() => runScriptMut.mutate(s)}
                  className="w-full text-left px-3 py-2 rounded-md hover:bg-slate-50 border border-slate-200 text-sm flex items-center justify-between"
                  data-testid={`script-${s.id}`}
                >
                  <span>{t(`farm.scripts.${s.id}`)}</span>
                  <Play size={14} className="text-brand-primary" />
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-4 pt-4 border-t border-slate-100">
            <h4 className="text-sm font-semibold text-brand-ink mb-2">{t('farm.scripts.customTitle')}</h4>
            <p className="text-xs text-brand-muted">{t('farm.scripts.customHint')}</p>
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
              <div className="p-2 border border-dashed border-slate-300 rounded text-center">Action</div>
              <div className="p-2 border border-dashed border-slate-300 rounded text-center">Delay</div>
              <div className="p-2 border border-dashed border-slate-300 rounded text-center">Condition</div>
              <div className="p-2 border border-dashed border-slate-300 rounded text-center">Loop</div>
            </div>
          </div>
        </aside>

        {/* Center: Accounts table */}
        <div className="card lg:col-span-3 overflow-x-auto">
          <h3 className="text-lg font-semibold text-brand-ink mb-3">{t('farm.table.title')}</h3>
          {accounts.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-12 bg-slate-100 animate-pulse rounded" />
              ))}
            </div>
          ) : !accounts.data || accounts.data.length === 0 ? (
            <p className="text-brand-muted text-sm">{t('farm.table.empty')}</p>
          ) : (
            <table className="w-full text-sm" data-testid="accounts-table">
              <thead>
                <tr className="text-left text-xs text-brand-muted border-b border-slate-100">
                  <th className="py-2">{t('farm.table.username')}</th>
                  <th className="py-2">{t('farm.table.status')}</th>
                  <th className="py-2">{t('farm.table.proxy')}</th>
                  <th className="py-2">{t('farm.table.warming')}</th>
                  <th className="py-2">{t('farm.table.lastAction')}</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {accounts.data.map((a) => (
                  <tr
                    key={a.id}
                    className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer"
                    onClick={() => setActiveAccount(a)}
                    data-testid={`account-row-${a.id}`}
                  >
                    <td className="py-2 font-medium text-brand-primary">{a.username}</td>
                    <td className="py-2"><StatusBadge status={a.status} /></td>
                    <td className="py-2 text-brand-muted">
                      {a.proxy.type} {a.proxy.country ? `· ${a.proxy.country}` : ''}
                    </td>
                    <td className="py-2">
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-1.5 bg-slate-100 rounded">
                          <div
                            className="h-full bg-amber-500 rounded"
                            style={{ width: `${Math.min(100, (a.warming_day / 30) * 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-brand-muted">{a.warming_day}/30</span>
                      </div>
                    </td>
                    <td className="py-2 text-xs text-brand-muted">
                      {dayjs(a.last_action_at).format('DD MMM HH:mm')}
                    </td>
                    <td className="py-2">
                      <button
                        aria-label={t('farm.table.actions')}
                        className="p-1.5 rounded hover:bg-slate-100"
                        onClick={(e) => { e.stopPropagation(); setActiveAccount(a); }}
                      >
                        <MoreVertical size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Proxy pool widget */}
      <div className="card mt-6" data-testid="proxy-pool">
        <h3 className="text-lg font-semibold text-brand-ink mb-3">{t('farm.proxy.title')}</h3>
        {proxyPool.data && proxyPool.data.providers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {proxyPool.data.providers.map((p) => {
              const Icon = p.kind === 'residential' ? Wifi : p.kind === 'datacenter' ? Server : Smartphone;
              return (
                <div key={`${p.name}-${p.kind}`} className="p-3 rounded-md border border-slate-100 flex items-center gap-3">
                  <Icon className="text-brand-primary" size={20} />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-brand-ink">{p.name}</div>
                    <div className="text-xs text-brand-muted">
                      {t('farm.proxy.success')}: {(p.success_rate * 100).toFixed(1)}% · ${p.cost_today.toFixed(2)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-brand-muted text-sm">{t('farm.proxy.empty')}</p>
        )}
      </div>

      {activeAccount && (
        <AccountDetailsPanel account={activeAccount} onClose={() => setActiveAccount(null)} />
      )}
    </div>
  );
}
